/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useAuth } from 'contexts/authContext';
import {
	KnowledgeBaseArticleContentItem,
	KnowledgeBaseArticleInfo,
	KnowledgeBaseArticleContentItemMeta,
	KnowledgeBaseArticlePresentationResult,
	KnowledgeBaseArticleVisitDetails,
	KnowledgeBaseDirectoryMeta,
	KnowledgeBaseDirectoryTranslated,
	KnowledgeBaseRecommendedArticlesByCategory,
} from 'types/KnowledgeBase';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Loader } from 'components/_new/Loader';
import { MenuItem } from 'primereact/menuitem';
import { ErrorContainer } from 'components/_new/ErrorContainer/ErrorContainer';
import { slugify } from 'utils/helpers';

type KnowledgeBaseQueries = {
	getArticleById: (
		articleId: number,
		params?: Record<string, string | null>
	) => Promise<KnowledgeBaseArticlePresentationResult>;
	getArticleInfo?: (articleId: number) => Promise<KnowledgeBaseArticleInfo>;
	getProgramArticleVisitsDetails?: (articleId: number) => Promise<KnowledgeBaseArticleVisitDetails[]>;
	getArticlesByDirectory: (
		directoryId: number,
		searchValue?: string | null
	) => Promise<KnowledgeBaseArticleContentItem[]>;
	getArticlesByTag: (tagId: number, searchValue?: string | null) => Promise<KnowledgeBaseArticleContentItem[]>;
	getArticlesBySearch: (searchValue: string | null) => Promise<KnowledgeBaseArticleContentItem[]>;
	getDirectories: () => Promise<KnowledgeBaseDirectoryTranslated[]>;
	getRecommendedArticles: () => Promise<KnowledgeBaseRecommendedArticlesByCategory>;
	getProgramOrganizersArticles: () => Promise<KnowledgeBaseArticleContentItem[]>;
	getArticlesByLabelsOrIds: (labels: Array<string | number>) => Promise<KnowledgeBaseArticleContentItemMeta[]>;
	getDirectoryByLabels: (labels: string[]) => Promise<Record<string, KnowledgeBaseDirectoryMeta>>;
};

// eslint-disable-next-line @typescript-eslint/ban-types
type KnowleedgeBaseContextProviderProps = PropsWithChildren<{
	context: string;
	linkPrefix: string;
	useSlugReplacer?: boolean;
	queries: KnowledgeBaseQueries;
}>;

type KnowledgeBaseType = {
	context: string;
	directories: KnowledgeBaseDirectoryTranslated[];
	currentDirectory: KnowledgeBaseDirectoryTranslated | null;
	pathParams: {
		directory: { id: number; slug?: string } | null;
		article: { id: number; slug?: string } | null;
		tag: { id: number; slug?: string } | null;
	};
	isMainPage: boolean;
	isArticlePage: boolean;
	article: KnowledgeBaseArticleContentItem | null;
	setArticle: (articleContentItem: KnowledgeBaseArticleContentItem | null) => void;
	setArticleLanguage: (language: string) => void;
	isSearch: boolean;
	search: {
		tag: number | null;
		search: string | null;
	};
	breadcrumbHome: MenuItem;
	baseBreadcrumbModel: MenuItem[];
	articleQuery: UseQueryResult<KnowledgeBaseArticlePresentationResult>;
	goToHome: () => void;
	knowledgeBaseLinkPrefix: string;
	// forceLoadArticleInSpecificLanguage: (lang: string) => void;
	onSearch: (value: string) => void;
	searchValue: string | null;
	queries: KnowledgeBaseQueries;
	generateDirectoryLink: (id: number, slug?: string) => string;
	directoriesQuery: UseQueryResult<KnowledgeBaseDirectoryTranslated[]>;
	setBreadcrumbs: (items: MenuItem[]) => void;
	isDirectory: boolean;
};

const KnowledgeBaseContext = createContext<KnowledgeBaseType>({} as KnowledgeBaseType);

const getArticleKey = (item: KnowledgeBaseArticleContentItem | null | undefined): string =>
	JSON.stringify({
		id: item?.id ?? null,
		slug: item?.slug ?? null,
		title: item?.title ?? null,
	});

const getBreadcrumbsKey = (items: MenuItem[] = []): string =>
	JSON.stringify(
		items.map((item) => ({
			id: item.id ?? null,
			label: item.label ?? null,
			url: item.url ?? null,
		}))
	);

export const KnowleedgeBaseContextProvider = ({
	context,
	useSlugReplacer = false,
	linkPrefix,
	queries,
	children,
}: KnowleedgeBaseContextProviderProps) => {
	const navigate = useNavigate();
	const { currentUser } = useAuth();

	const { '*': restPath } = useParams();
	const pathParams = useMemo(() => {
		const parts = restPath ? restPath.split('/') : [];
		const splitIdentifierAndSlug = (input: string): { id: number; slug: string } | null => {
			const match = input.match(/^(\d+)(?:-(.*))?$/);
			if (match) {
				const id = parseInt(match[1], 10);
				const slug = match[2] ?? null; // jeśli brak slug, zwracamy pusty string
				return { id, slug };
			}
			return null;
		};

		if (parts[0] === 'tag') {
			const tag = parts[1] || null;
			return {
				tag: tag ? splitIdentifierAndSlug(tag) : null,
				directory: null,
				article: null,
			};
		}

		if (parts[0] === 'article') {
			const article = parts[1] || null;
			return {
				tag: null,
				directory: null,
				article: article ? splitIdentifierAndSlug(article) : null,
			};
		}

		if (parts[0] === 'directory') {
			const directory = parts[1] || null;
			return {
				tag: null,
				directory: directory ? splitIdentifierAndSlug(directory) : null,
				article: null,
			};
		}

		return {
			tag: null,
			directory: null,
			article: null,
		};
	}, [restPath]);

	const isMainPage = !pathParams.directory && !pathParams.article && !pathParams.tag;

	const [article, setArticleState] = useState<KnowledgeBaseArticleContentItem | null>(null);
	const setArticle = useCallback((item: KnowledgeBaseArticleContentItem | null) => {
		setArticleState((prevItem) => {
			const prevKey = getArticleKey(prevItem);
			const nextKey = getArticleKey(item);
			return prevKey === nextKey ? prevItem : item;
		});
	}, []);
	const isArticlePage = !pathParams.directory && !!pathParams.article && !pathParams.tag;
	const isDirectory = !!pathParams.directory && !pathParams.article && !pathParams.tag;

	const [searchParams, setSearchParams] = useSearchParams();
	const search = useMemo(() => {
		const tagQuery = searchParams.get('tag');
		const searchQuery = searchParams.get('search');
		return {
			tag: tagQuery && tagQuery.length > 0 ? Number(tagQuery) : null,
			search: searchQuery && searchQuery.length > 0 ? searchQuery : null,
		};
	}, [searchParams]);

	const directoriesQuery = useQuery(
		[
			'KNOWLEDGE_BASE_DIRECTORIES',
			{
				context,
				userId: Number(currentUser?.id),
				language: currentUser?.language,
				isProfessionalViewEnabled: currentUser?.isProfessionalViewEnabled,
			},
		],
		() => queries.getDirectories(),
		{
			enabled: true,
		}
	);

	const directories = directoriesQuery.data || [];
	const currentDirectory = pathParams.directory
		? directories.find((directory) => directory.id === pathParams.directory?.id) || null
		: null;

	useEffect(() => {
		if (useSlugReplacer) {
			if (currentDirectory && currentDirectory.slug !== pathParams.directory?.slug) {
				navigate(
					`${linkPrefix}/directory/${currentDirectory.id}${
						currentDirectory.slug ? `-${slugify(currentDirectory.slug)}` : ''
					}`,
					{ replace: true }
				);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [useSlugReplacer, currentDirectory, pathParams]);

	const [articleLanguage, setArticleLanguage] = useState<string | null>(searchParams.get('lang'));
	useEffect(() => {
		const langFromParam = searchParams.get('lang');
		if (langFromParam && langFromParam !== articleLanguage) {
			setArticleLanguage(langFromParam);
		} else if (!langFromParam || langFromParam === '') {
			searchParams.delete('lang');
		}
	}, [articleLanguage, searchParams]);
	// const forceLoadArticleInSpecificLanguage = (lang: string) => {
	// 	setArticleLanguage(lang);
	// };

	const articleQuery = useQuery(
		[
			'KNOWLEDGE_BASE_ARTICLE',
			{
				context,
				userId: Number(currentUser?.id),
				// applicationId: Number(currentApplicationData.id),
				articleId: pathParams.article?.id,
				language: currentUser?.language,
				articleLanguage,
				isProfessionalViewEnabled: currentUser?.isProfessionalViewEnabled,
			},
		],
		// () => knowledgeBaseApi.getArticleById(Number(pathParams.article?.id), { lang: articleLanguage }),
		() =>
			queries.getArticleById(Number(pathParams.article?.id), {
				lang: articleLanguage,
			}),
		{
			enabled: Boolean(pathParams.article),
			onSuccess: ({ articleContent }) => {
				// setArticle({ id: articleContent.articleId, slug: articleContent.slug, title: articleContent.title });
				if (useSlugReplacer) {
					if (articleContent && articleContent.slug !== pathParams.article?.slug) {
						navigate(
							`${linkPrefix}/article/${articleContent.articleId}${
								articleContent.slug ? `-${slugify(articleContent.slug)}` : ''
							}`,
							{ replace: true }
						);
					}
				}
			},
		}
	);
	useEffect(() => {
		if (!pathParams.article?.id) {
			articleQuery.remove();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pathParams.article?.id]);

	const refetchBase = () => {
		directoriesQuery.refetch();
	};

	const searchValue = search.search;
	const onSearch = useCallback(
		(text: string) => {
			const nextSearchValue = text.trim();
			const nextSearchParams = new URLSearchParams(searchParams);

			if (nextSearchValue.length > 0) {
				nextSearchParams.set('search', nextSearchValue);
			} else {
				nextSearchParams.delete('search');
			}

			setSearchParams(nextSearchParams, { replace: true });
		},
		[searchParams, setSearchParams]
	);
	const isSearch = Boolean(searchValue && String(searchValue).length > 0);

	const goToHome = () => {
		navigate({ pathname: linkPrefix, search: '' });
	};

	const [breadcrums, setBreadcrumsState] = useState<MenuItem[]>([]);
	const setBreadcrumbs = useCallback((items: MenuItem[]) => {
		setBreadcrumsState((prevItems) => {
			const prevKey = getBreadcrumbsKey(prevItems);
			const nextKey = getBreadcrumbsKey(items);
			return prevKey === nextKey ? prevItems : items;
		});
	}, []);
	const breadcrumbHome: MenuItem = {
		icon: 'pi pi-book',
		label: 'Home',
		command: (event: any) => {
			event.preventDefault();
			goToHome();
		},
	};

	const generateDirectoryLink = (id: number, slug?: string) =>
		`${linkPrefix}/directory/${id}${slug ? `-${slugify(slug)}` : ''}`;

	if (!directoriesQuery.isFetched) {
		return <Loader />;
	}

	if (directoriesQuery.isError) {
		return <ErrorContainer action={refetchBase} error={directoriesQuery.error} />;
	}

	const baseBreadcrumbModel: MenuItem[] = [
		currentDirectory
			? {
					id: `directory-${currentDirectory.id}`,
					label: currentDirectory.name,
					command: () => {
						const slug = currentDirectory.slug || null;
						navigate(`${linkPrefix}/directory/${currentDirectory.id}${slug ? `-${slugify(slug)}` : ''}`);
					},
				}
			: null,
		...(articleQuery?.data?.articleContent
			? [
					...(articleQuery.data.directory
						? [
								{
									id: `directory-${articleQuery.data.directory.id}`,
									label: articleQuery.data.directory.name,
									command: () => {
										const slug = articleQuery.data.directory?.slug || null;
										navigate(
											`${linkPrefix}/directory/${articleQuery.data.directory?.id}${
												slug ? `-${slugify(slug)}` : ''
											}`
										);
									},
								},
							]
						: []),
					{
						id: `article-${articleQuery.data.articleContent.articleId}`,
						label: articleQuery.data.articleContent.title,
						command: () => {
							const slug = articleQuery.data.articleContent?.slug || null;
							navigate(
								`${linkPrefix}/article/${articleQuery.data.articleContent?.articleId}${
									slug ? `-${slugify(slug)}` : ''
								}`
							);
						},
					},
				]
			: []),
		...breadcrums,
		// ...(searchValue ? [{ id: `search`, label: t('knowledgeBase.searchResultsFor', { query: searchValue }) }] : []),
	].filter(Boolean) as MenuItem[];

	return (
		<KnowledgeBaseContext.Provider
			value={{
				context,
				directories,
				currentDirectory,
				pathParams,
				isMainPage,
				isArticlePage,
				article,
				setArticleLanguage,
				setArticle,
				setBreadcrumbs,
				isSearch,
				isDirectory,
				articleQuery,
				search,
				breadcrumbHome,
				baseBreadcrumbModel,
				goToHome,
				knowledgeBaseLinkPrefix: linkPrefix,
				onSearch,
				searchValue,
				queries,
				generateDirectoryLink,
				directoriesQuery,
			}}
		>
			{children}
		</KnowledgeBaseContext.Provider>
	);
};

type UseKnowledgeBaseOptions = {
	article?: KnowledgeBaseArticleContentItem;
	breadcrums?: MenuItem[];
};

export const useKnowledgeBase = (options?: UseKnowledgeBaseOptions) => {
	const ctx = useContext(KnowledgeBaseContext);
	const hasArticleOption = Boolean(options && Object.prototype.hasOwnProperty.call(options, 'article'));
	const article = options?.article;
	const articleKey = useMemo(() => getArticleKey(article), [article]);
	const hasBreadcrumbsOption = Boolean(options && Object.prototype.hasOwnProperty.call(options, 'breadcrums'));
	const breadcrumbs = options?.breadcrums;
	const breadcrumbsKey = useMemo(() => getBreadcrumbsKey(breadcrumbs ?? []), [breadcrumbs]);

	useEffect(() => {
		if (hasArticleOption) {
			if (article) {
				ctx.setArticle(article);
			} else {
				ctx.setArticle(null);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasArticleOption, articleKey]);

	useEffect(
		() => () => {
			if (hasArticleOption) {
				ctx.setArticle(null);
			}
		},
		[hasArticleOption, ctx.setArticle]
	);

	useEffect(() => {
		if (hasBreadcrumbsOption) {
			if (breadcrumbs) {
				ctx.setBreadcrumbs(breadcrumbs);
			} else {
				ctx.setBreadcrumbs([]);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasBreadcrumbsOption, breadcrumbsKey]);

	useEffect(
		() => () => {
			if (hasBreadcrumbsOption) {
				ctx.setBreadcrumbs([]);
			}
		},
		[hasBreadcrumbsOption, ctx.setBreadcrumbs]
	);

	return ctx;
};
